import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { storeApi, useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { ProductCardMini } from "./Home";
import { Search, Filter, X, PawPrint } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function ProductList() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const sort = params.get("sort") || "newest";
  const page = parseInt(params.get("page") || "1");
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState(q);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { addToCart } = useStore();

  useDocumentTitle(
    category ? `${category} for pets` : q ? `Search results for "${q}"` : "Shop pet essentials",
    "Browse premium pet food, treats, toys, grooming tools and more. Free shipping over ₹1,499."
  );

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => { storeApi.categories().then(setCats); storeApi.brands().then(setBrands); }, []);
  useEffect(() => {
    storeApi.products({ q, category, brand, sort, page, page_size: 24 }).then(setData);
  }, [q, category, brand, sort, page]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    next.delete("page");
    setParams(next);
  };

  const onSearch = (e) => { e.preventDefault(); setParam("q", search.trim()); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4" data-testid="product-list-page">
      <div className="flex items-center gap-2">
        <form onSubmit={onSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search food, treats, toys, brands…"
            className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white border border-stone-200 text-sm focus:outline-none focus:border-amber-400"
            data-testid="store-search"
          />
        </form>
        <button onClick={() => setFiltersOpen(v => !v)} className="px-4 py-3 rounded-2xl bg-white border border-stone-200 text-sm font-semibold flex items-center gap-1" data-testid="filters-toggle">
          <Filter className="w-4 h-4" />Filters
        </button>
      </div>

      {filtersOpen && (
        <div className="mt-3 grid sm:grid-cols-3 gap-3 bg-white p-4 rounded-2xl border border-stone-200" data-testid="filters-panel">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Category</label>
            <select value={category} onChange={e => setParam("category", e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm" data-testid="filter-category">
              <option value="">All</option>
              {cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Brand</label>
            <select value={brand} onChange={e => setParam("brand", e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm" data-testid="filter-brand">
              <option value="">All</option>
              {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Sort</label>
            <select value={sort} onChange={e => setParam("sort", e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm" data-testid="filter-sort">
              <option value="newest">Newest</option>
              <option value="price_asc">Price · Low → High</option>
              <option value="price_desc">Price · High → Low</option>
              <option value="popular">Popular</option>
            </select>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      <div className="mt-3 flex flex-wrap gap-2">
        {category && <Chip label={category} onClear={() => setParam("category", "")} testid="chip-category" />}
        {brand && <Chip label={brand} onClear={() => setParam("brand", "")} testid="chip-brand" />}
        {q && <Chip label={`"${q}"`} onClear={() => setParam("q", "")} testid="chip-q" />}
      </div>

      <div className="mt-2 text-xs text-stone-500">{data.total} item{data.total === 1 ? "" : "s"}</div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="product-grid">
        {data.items.map(p => (
          <div key={p.id} className="bg-transparent">
            <ProductCardMini p={p} onAdd={() => addToCart(p, 1)} />
          </div>
        ))}
        {data.items.length === 0 && (
          <div className="col-span-full text-center py-12 text-stone-500">
            <PawPrint className="w-12 h-12 mx-auto text-stone-300 mb-2" />
            <div className="font-heading text-xl">No products match</div>
            <div className="text-sm">Try a different search or clear filters.</div>
          </div>
        )}
      </div>

      {data.total > 24 && (
        <Pagination total={data.total} page={page} pageSize={24} onPage={(p) => setParam("page", String(p))} />
      )}
    </div>
  );
}

const Chip = ({ label, onClear, testid }) => (
  <button onClick={onClear} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold" data-testid={testid}>
    {label}<X className="w-3 h-3" />
  </button>
);

function Pagination({ total, page, pageSize, onPage }) {
  const pages = Math.ceil(total / pageSize);
  return (
    <div className="mt-6 flex items-center justify-center gap-2" data-testid="pagination">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-3 py-1.5 rounded-md border border-stone-200 text-sm disabled:opacity-40">Prev</button>
      <span className="text-sm text-stone-500">Page {page} of {pages}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-3 py-1.5 rounded-md border border-stone-200 text-sm disabled:opacity-40">Next</button>
    </div>
  );
}
